import React, { useEffect, useMemo, useState } from 'react';
import { CustomSelect } from 'web-library';
import { KeyValueResponse } from '../../api';
import { useGetOrganisationsQuery } from '../../api/account-management/useGetOrganisationsQuery';
import { MenuItems } from './model';
import { SelectChangeEvent } from '@mui/material';

export interface OrganisationMultiSelectProps {
  /** to handle select on change event
   * */
  onChange?: (event: KeyValueResponse[]) => void;

  /** to define select control selected value
   * */
  value: string[];

  /** to define option label
   * */
  label: string;

  /** to define fitler option spacing
   * */
  filterOptionSpacing: string;
}

/** reusable select component for organisation option
 * */
const OrganisationMultiSelect: React.FC<OrganisationMultiSelectProps> = (
  props: OrganisationMultiSelectProps
) => {
  const {
    onChange = (_event: KeyValueResponse[]) => {
      // do nothing
    },
    value = [],
    label,
    filterOptionSpacing,
  } = props;
  const [organisations, setOrganisations] = useState<KeyValueResponse[]>([]);

  const { refetch } = useGetOrganisationsQuery({
    enabled: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    onSuccess: (responseData) => {
      if (responseData && responseData.length > 0) {
        setOrganisations(responseData);
      }
    },
  });

  /** reform data into CustomSelect MenuItems
   * */
  const dropDownData = useMemo<MenuItems[]>(() => {
    if (!organisations) {
      return [];
    }
    return organisations.map((option) => ({
      children: option.description,
      value: option.code,
      key: `k-${option.code}`,
    }));
  }, [organisations]);

  /** handle select on Change
   * */
  const onSelectOptionChange = async (selectedValue: any[] | unknown) => {
    if (organisations && organisations.length > 0 && Array.isArray(selectedValue)) {
      onChange(organisations.filter((organisation) => selectedValue.includes(organisation.code)));
    }
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <CustomSelect
      className={filterOptionSpacing}
      displayEmpty
      variant="filled"
      multiple
      label={label}
      labelId="inputSelectLabel-organisations"
      id="inputSelect-organisations"
     onChange={(e: SelectChangeEvent<unknown>) => {
        onSelectOptionChange(e.target.value);
        window.dispatchEvent(new CustomEvent('resize'));
      }}
      menuItems={dropDownData}
      value={value}
      renderValue={(selected: unknown | string[]) =>
        Array.isArray(selected) && selected.length > 0
          ? organisations
              .filter((organisation) => selected.includes(organisation.code))
              .map((s) => s.description)
              .join(', ')
          : 'Select private centre/school'
      }
    />
  );
};

export default OrganisationMultiSelect;
